import React, { useEffect, useRef, useState } from "react";
import { Layout, Menu } from "antd";
import "assets/styles/sidebar.scss";
import type { MenuProps } from "antd";

import { ReactComponent as Pen } from "assets/icons/pen1.svg";
import { ReactComponent as Jobs } from "assets/icons/jobs.svg";
import { ReactComponent as Dashboard } from "assets/icons/dashboard.svg";
import { ReactComponent as Saved } from "assets/icons/saved.svg";
import { ReactComponent as Profile } from "assets/icons/profile.svg";
import { ReactComponent as History } from "assets/icons/history.svg";
import { ReactComponent as Logout } from "assets/icons/logout.svg";
import { ReactComponent as Checkbook } from "assets/icons/checkbook.svg";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import GetToken from "services/utils/getToken";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateProfilePhoto } from "services/apis/dashboard";
import ImageUpload from "./ImageUpload";
import { useAppConfig } from "hooks/useAppConfig";

const { SubMenu } = Menu;
const { Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

function getItem(label: React.ReactNode, key: React.Key, icon?: React.ReactNode, children?: MenuItem[]): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

const Sidebar = () => {
  const { token, isSchool, school_logo, full_name, school_name } = GetToken();
  const { data: appConfig } = useAppConfig();
  const showTeacherPricing = appConfig?.teacher_application_paywall_enabled ?? false;
  const [width, setWidth] = useState<any>(window.innerWidth);
  const [collapsed, setCollapsed] = useState(width < 992);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation<void, Error, any>({
    mutationFn: (photo) => {
      return updateProfilePhoto(photo, token);
    },
    onSuccess: (content: any) => {
      const newPhoto = content.data.school_logo;
      // Update the cached login data immutably
      queryClient.setQueryData(["login"], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            user: {
              ...oldData.data.user,
              school_logo: newPhoto,
            },
          },
        };
      });
    },
  });
  const handlePhotoUpload = (photo: any) => {
    mutation.mutate(photo);
  };
  const items: MenuItem[] = [
    getItem("Dashboard", "/dashboard/home", <Dashboard className="max-w-6 w-full h-6" />),
    getItem("My Profile", "/dashboard/my-profile", <Profile className="max-w-6 w-full h-6" />),
  ];
  if (isSchool) {
    // Remove items if isSchool is true
    items.push(
      getItem("My Jobs", "/dashboard/jobs", <Jobs className="max-w-6 w-full h-6" />),
      getItem("Submit Job", "/dashboard/submit-job", <Pen className="max-w-6 w-full h-6" />),
      getItem("Pricing", "/dashboard/pricing", <Checkbook className="max-w-6 w-full h-6 pricing" />)
    );
  } else {
    // Remove items if isSchool is false
    items.push(
      getItem("Saved Jobs", "/dashboard/saved-jobs", <Saved className="max-w-6 w-full h-6" />),
      getItem("My Applications", "/dashboard/my-applications", <History className="max-w-6 w-full h-6" />)
    );
    if (showTeacherPricing) {
      items.push(getItem("Pricing", "/dashboard/pricing", <Checkbook className="max-w-6 w-full h-6 pricing" />));
    }
    items.push(getItem("CV Upgrade", "/cv-services", <Pen className="max-w-6 w-full h-6" />));
  }
  items.push(
    getItem("FAQ", "/faq", <Checkbook className="max-w-6 w-full h-6" />),
    getItem("Contact", "/contact", <Profile className="max-w-6 w-full h-6" />)
  );

  const isMenuItemActive = (key: string, children?: MenuItem[]): boolean => {
    if (children) {
      // Check if any of the children is active
      return children.some((child) => isMenuItemActive((child as any).key));
    }
    // Check if the current item is active based on its key and the pathname
    return location.pathname.startsWith(`${key}`);
  };

  const renderMenuItems = (items: MenuItem[]) => {
    return items.map((item: any) => {
      if (item.children) {
        return (
          <SubMenu
            key={item.key}
            icon={item.icon}
            title={item.label}
            // className={isMenuItemActive(item.key) ? `active` : ""}
            className={isMenuItemActive(item.key, item.children) ? `active` : ""}
          >
            {renderMenuItems(item.children)}
          </SubMenu>
        );
      }

      return (
        <Menu.Item
          key={item.key}
          icon={item.icon}
          className={isMenuItemActive(item.key, item.children) ? `active` : ""}
        >
          <Link to={item.key}>{item.label}</Link>
        </Menu.Item>
      );
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWidth(newWidth);
      setCollapsed(newWidth < 992);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const fileInputRef: any = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files[0];
    handlePhotoUpload(selectedFile);
  };

  const handleLogout = () => {
    queryClient.invalidateQueries({ queryKey: ["login"] });
    localStorage.removeItem("teacher-portal");
    queryClient.clear();
    navigate("/login");
  };
  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => {
        if (width > 991 || window.innerWidth > 991) {
          setCollapsed(value);
        }
      }}
      className="sidebar"
    >
      <div className="demo-logo-vertical" />
      <div
        className={`absolute top-0 left-0 w-full bg-[rgb(232,232,232)] right-0 mx-auto overflow-hidden transition-all ease-in duration-700 ${
          collapsed ? "rounded-2xl" : "rounded-[37px]"
        }`}
      >
        <div
          className={`w-full h-full flex justify-center items-center flex-col transition-all ease-in duration-700 ${
            collapsed ? "py-3 gap-0" : "py-6 gap-6"
          }`}
        >
          <ImageUpload
            collapsed={collapsed}
            isSchool={isSchool}
            handleButtonClick={handleButtonClick}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            school_logo={school_logo}
            school_name={school_name}
            full_name={full_name}
          />
          <div className={`overflow-hidden transition-all ease-in duration-700 ${collapsed ? "max-h-0" : "max-h-20"}`}>
            <h6
              className={`text-xl text-black font-montserrat font-medium transition-all ease-in duration-700 text-center capitalize ${
                collapsed ? "opacity-0" : "opacity-100"
              }`}
            >
              {isSchool ? school_name : full_name}
              {/* {isSchool ? userProfileQueryState?.data?.school_name : userProfileQueryState?.data?.user?.username} */}
            </h6>
          </div>
        </div>
      </div>
      <Menu theme="dark" mode="inline">
        {renderMenuItems(items)}
      </Menu>
      <button
        className="absolute bottom-20 z-40 left-7 flex justify-start items-center cursor-pointer gap-1.5 font-montserrat text-[#244034] text-base font-medium"
        onClick={handleLogout}
      >
        <Logout />
        <span className={`transition-opacity ease-in duration-700 ${collapsed ? "opacity-0" : "opacity-100"}`}>
          Logout
        </span>
      </button>
    </Sider>
  );
};

export default Sidebar;
